// fetchLeaderboardUsers.js
'use strict';
import parseLinkHeader from 'parse-link-header';

import fetch from '../resource/customFetch.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import handleFetchError from '../resource/handleFetchError.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import { actionLog } from '../resource/debug.js';
import { RANGES } from '../resource/leaderboardConstants.js';
import selectPath from '../resource/selectPath.js';
import getNetworkingData from '../selector/getNetworkingData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { LEADERBOARD_CATEGORIES } from '../RemoteConfigKeys.js';

import {
  ADD_LIST_ITEMS,
  SET_LIST_ITEMS,
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
  CLEAR_NETWORKING_NODES,
} from '../ActionTypes.js';

const AVALIABLE_DATE_RANGE = RANGES;
const ONE_MINUTE_MSECS = 1000 * 60;

/**
 * Fetch leaderboard users
 * @kind action
 * @param {object} payload - action payload.
 * @param {string} payload.category - leaderboard category.
 * @param {string} payload.range - time range.
 * @param {string} payload.region - user region.
 * @param {number} [payload.page = 1] - page index.
 * @param {number} [payload.limit = 10] - item amount per page.
 * @param {object} {[httpProxyHeaders = {}]} - http proxy headers for SSR.
 * @return {Promise} Action promise.
 */
const fetchLeaderboardUsers =
  ({ category, range, region, page = 1, limit = 10, httpProxyHeaders = {} }) =>
  async (dispatch, getState) => {
    const state = getState();
    const categories = getRemoteConfigData(state, LEADERBOARD_CATEGORIES);

    const isValidCategory = categories.includes(category);
    const isValidRange = AVALIABLE_DATE_RANGE.includes(range);

    if (!isValidCategory || !isValidRange) {
      actionLog(
        `invalid category, range or region, current category is: ${category}, range is: ${range}, region is: ${region}.`,
        'error occur at action/fetchLeaderboardUsers.js'
      );
      return dispatch({ type: '' });
    }

    const listSelectPath = selectPath.list.leaderboard({
      region,
      category,
      range,
    });
    const networkingSelectPath = selectPath.networking.leaderboard({
      region,
      category,
      range,
      page,
    });

    const lastFetchedTimestamp =
      getNetworkingData(state, networkingSelectPath, 'fetchedTimestamp') || 0;
    const shouldFetchNewData =
      Date.now() >= lastFetchedTimestamp + ONE_MINUTE_MSECS;

    if (!shouldFetchNewData) {
      return dispatch({ type: '' });
    }

    const fetchOptions = {
      method: 'GET',
      headers: {
        ...getHeaders(),
        ...httpProxyHeaders,
      },
    };
    const isFirstPage = page === 1;
    const url = getResourceUrl({ endpoint: '/leaderboard' });

    url.searchParams.append('region-group', region);
    url.searchParams.append('category', category);
    url.searchParams.append('grouping', range);
    url.searchParams.append('page', page);
    url.searchParams.append('limit', limit);

    dispatch({
      type: SET_NETWORKING_FETCHING,
      payload: { selectPath: networkingSelectPath },
    });

    try {
      const response = await fetch(url.href, fetchOptions);

      if (!response.ok) {
        await handleFetchError({ response });
      }

      let nextPage = null;
      let lastPage = null;

      const data = await response.json();
      const totalCount = Number(response.headers.get('X-Total-Count'));
      const links = parseLinkHeader(response.headers.get('Link'));
      const userIds = data.map(user => user.id);

      if (links && links.next) {
        nextPage = Number(links.next.page);
      }
      if (links && links.last) {
        lastPage = Number(links.last.page);
      }

      // first page use SET to update list items, so need to invalid all cache.
      // avoiding can not fetch other page's data by this mechanism.
      // more detail here: https://github.com/swaglive/swag-webapp/issues/1271
      if (isFirstPage) {
        dispatch({
          type: CLEAR_NETWORKING_NODES,
          payload: {
            selectPaths: [networkingSelectPath.slice(0, -1)],
          },
        });
      }

      dispatch({
        type: isFirstPage ? SET_LIST_ITEMS : ADD_LIST_ITEMS,
        payload: {
          selectPath: listSelectPath,
          itemIds: userIds,
          totalCount,
          nextPage,
          lastPage,
        },
      });
      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath: networkingSelectPath },
      });
    } catch (error) {
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath: networkingSelectPath, error },
      });
    }
  };

export default fetchLeaderboardUsers;
